/**
 * harness/installPrompt.js
 * T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #11「PWA:manifest/Service Worker」
 *
 * 缺口诊断(实测,不是猜测):
 *   `grep -rn "beforeinstallprompt" vite/src/` → **0 命中**。
 *   而 `vite/public/manifest.json` 存在、`vite/src/main.jsx` 也已经注册了
 *   Service Worker ⇒ PWA 的"能装"那一半早就齐了,缺的只是**装得进去的那扇门**:
 *   浏览器触发 `beforeinstallprompt` 时没人接,事件就被丢弃,用户永远看不到
 *   "安装到桌面"入口 —— 只能自己去浏览器菜单里找,绝大多数人不会找。
 *
 * 实现要点(这几个坑都踩过):
 *
 *  ① `beforeinstallprompt` 必须**提前**注册。
 *     浏览器在满足安装条件(SW 已激活 + 有 manifest + 有 HTTPS)后才派发它,
 *     而且**只派发一次**。如果组件挂载太晚(用户已经在页面上了),事件早就
 *     派发完、永远不会重来 ⇒ 按钮永远是灰的。
 *     ⇒ 所以这里在**模块加载时**就注册(window 级),而不是在组件的 useEffect 里。
 *
 *  ② 调用 `prompt()` 之后,事件对象的 `userChoice` 才会 resolve。
 *     只 `await deferredPrompt.prompt()` 会拿不到用户选了"装"还是"不装"。
 *
 *  ③ `prompt()` 只能调用一次。调用前必须把保存的 deferredPrompt 清空,
 *     否则第二次调用会抛 InvalidStateError(而且这个异常是 async 的,
 *     不 catch 会变成 unhandledrejection)。
 *
 *  ④ 华为/小米等国产浏览器的 WebView 有两种情况:
 *     · 完全不派发 `beforeinstallprompt` ⇒ 按钮不显示(本模块返回 false),
 *       不给用户一个点了没反应的假按钮。
 *     · 派发了但 `prompt()` 无效(部分定制内核) ⇒ catch 掉,静默降级。
 *
 *  ⑤ 已经装成 standalone 的用户不该再看到"安装"按钮。
 *     用 `window.matchMedia('(display-mode: standalone)')` +
 *     `navigator.standalone`(iOS Safari)双重判断。
 */

/** 保存的 beforeinstallprompt 事件对象(只能 prompt 一次)。 */
let deferredPrompt = null;

/** 已经派发过 beforeinstallprompt(即使结果是不支持)。 */
let hasPromptEvent = false;

/** 已安装为 standalone / display-mode。 */
let isInstalled = false;

/** 订阅者集合(组件用)。 */
const listeners = new Set();

function notify() {
  const state = { canInstall: canInstall(), isInstalled };
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (_) {
      // 单个订阅者出错不能影响其它订阅者
    }
  });
}

/**
 * 当前是否处于「可以安装」状态。
 * @returns {boolean}
 */
export function canInstall() {
  return !!deferredPrompt && !isInstalled;
}

/**
 * 是否已安装为独立应用(standalone)。
 * @returns {boolean}
 */
export function isStandalone() {
  return isInstalled;
}

/**
 * 触发安装。
 *
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 *   · 'accepted'   —— 用户点了"安装"
 *   · 'dismissed'  —— 用户关闭了安装弹窗
 *   · 'unavailable'—— 当前环境不支持(没有 deferredPrompt / 重复调用 / 内核无效)
 */
export async function promptInstall() {
  // ③ 先清空:prompt() 只能调一次。
  const dp = deferredPrompt;
  if (!dp) return 'unavailable';
  deferredPrompt = null;

  try {
    await dp.prompt();
    // ② userChoice 才是真正的结果。
    const choice = dp.userChoice;
    const outcome = choice && choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
    if (outcome === 'accepted') {
      isInstalled = true;
    }
    notify();
    return outcome;
  } catch (_) {
    // ④ 国产 WebView / 已安装 / 用户手势失效 ⇒ 静默降级。
    notify();
    return 'unavailable';
  }
}

/**
 * 订阅安装状态变化。
 * @param {(state: {canInstall: boolean, isInstalled: boolean}) => void} fn
 * @returns {() => void} 取消订阅函数
 */
export function subscribeInstallState(fn) {
  listeners.add(fn);
  // 立即同步一次当前状态(避免订阅者要等一次事件才知道能不能装)。
  try {
    fn({ canInstall: canInstall(), isInstalled });
  } catch (_) {
    /* noop */
  }
  return () => listeners.delete(fn);
}

/**
 * 检测是否已安装为 standalone,并监听变化。
 * 只在模块加载时跑一次。
 */
function detectStandalone() {
  try {
    const mqStandalone =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone =
      typeof window !== 'undefined' &&
      (window.navigator.standalone === true ||
        (window.navigator.standalone === undefined && false));
    isInstalled = !!(mqStandalone || iosStandalone);
  } catch (_) {
    isInstalled = false;
  }
}

if (typeof window !== 'undefined') {
  detectStandalone();

  // ① 模块加载时就注册,不等组件挂载。
  window.addEventListener('beforeinstallprompt', (e) => {
    // 必须 preventDefault(),否则 Chrome 会弹自己的迷你安装条,
    // 和我们自己的按钮重复。
    try {
      e.preventDefault();
    } catch (_) {
      /* noop */
    }
    deferredPrompt = e;
    hasPromptEvent = true;
    notify();
  });

  // 用户真的装了(某些浏览器不派发 accepted,而是直接进 standalone)。
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    isInstalled = true;
    notify();
  });

  // 匹配到 standalone 时(用户从桌面图标打开)也更新一下。
  try {
    if (typeof window.matchMedia === 'function') {
      const mq = window.matchMedia('(display-mode: standalone)');
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', (e) => {
          isInstalled = !!e.matches;
          notify();
        });
      }
    }
  } catch (_) {
    /* noop */
  }
}

const InstallPrompt = {
  canInstall,
  isStandalone,
  promptInstall,
  subscribeInstallState,
  /** 是否收到过 beforeinstallprompt(用于诊断:为什么按钮没出现) */
  didGetPromptEvent: () => hasPromptEvent,
};

if (typeof window !== 'undefined') {
  window.InstallPrompt = InstallPrompt;
}

export default InstallPrompt;
